/* ==========================================================================
   Kenza Pro — Magnetic CTA
   Desktop: primary CTAs gently pull toward the cursor when within range.
   Mobile: no magnet (no cursor) — CSS handles tap-scale feedback instead.
   Auto-attaches to all .kp-btn--primary in viewport.
   ========================================================================== */

(() => {
  if (window.__kpMagnetBooted) return;
  window.__kpMagnetBooted = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  if (reduced || isTouch) return; // CSS handles mobile tap feedback

  // Allow merchants to disable globally via theme setting (rendered into DOM)
  if (document.documentElement.dataset.kpMagnetic === 'off') return;

  const RANGE = 110;     // px — activation radius from button center
  const STRENGTH = 0.16; // 0–1 — how strongly the button follows the cursor
  const MAX = 8;         // px — absolute cap on translate
  const EASE_IN  = 380;  // ms — settle-toward-cursor transition
  const EASE_OUT = 520;  // ms — return-to-rest transition

  const active = new Set();
  let rafPending = false;
  let cursorX = 0, cursorY = 0;

  const attach = (btn) => {
    if (btn.__kpMagnet) return;
    btn.__kpMagnet = true;
    btn.dataset.kpMagnet = '';
    btn.style.transition = `transform ${EASE_OUT}ms cubic-bezier(.2,.7,.2,1)`;
    active.add(btn);
  };

  const update = () => {
    rafPending = false;
    active.forEach(btn => {
      const r = btn.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = cursorX - cx;
      const dy = cursorY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist < RANGE) {
        // Soft falloff: closer = more pull
        const f = (1 - dist / RANGE) * STRENGTH;
        const tx = Math.max(-MAX, Math.min(MAX, dx * f));
        const ty = Math.max(-MAX, Math.min(MAX, dy * f));
        btn.style.transition = `transform ${EASE_IN}ms cubic-bezier(.2,.7,.2,1)`;
        btn.style.transform = `translate(${tx}px, ${ty}px)`;
      } else if (btn.style.transform) {
        btn.style.transition = `transform ${EASE_OUT}ms cubic-bezier(.2,.7,.2,1)`;
        btn.style.transform = '';
      }
    });
  };

  window.addEventListener('mousemove', (e) => {
    cursorX = e.clientX;
    cursorY = e.clientY;
    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });

  // Reset on mouse leave (cursor leaves window)
  document.addEventListener('mouseleave', () => {
    active.forEach(btn => {
      btn.style.transition = `transform ${EASE_OUT}ms cubic-bezier(.2,.7,.2,1)`;
      btn.style.transform = '';
    });
  });

  // ---------- Viewport gating via IntersectionObserver ----------
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) attach(e.target);
      else {
        active.delete(e.target);
        if (e.target.style.transform) e.target.style.transform = '';
      }
    }
  }, { rootMargin: '120px' });

  const scan = () => {
    document.querySelectorAll('.kp-btn--primary:not([data-kp-magnet-off])').forEach(btn => io.observe(btn));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
})();
